import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import TotalSalesLinearChart from "./payments";
import LocationsSalesChart from "./locations-sales";
import StatisticsBox from "./statistics-box";
import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { formatNumber, hoursDecimalToHM } from "src/utils";
import { useGetLocationsList } from "src/api/locations";
import { notificationsState } from "src/store/notifications";
import { useRecoilState } from "recoil";
import { authState } from "src/store/auth";
import TotalCarsChart from "./total-cars-chart";
import LocationsCarsChart from "./locations-cars";
import {
  useGetClientLocationsServices,
  useGetSalesSummary,
} from "src/api/sales";
import { DateTimePicker } from "@mui/x-date-pickers";
import LoadingButton from "@mui/lab/LoadingButton";
import { NoData } from "src/shared/components/tables/no-data";
import AvgStayLinearChart from "./avg-stay-linear-chart";
import AvgStayBreakDownChart from "./avg-stay-breakdown";
import FreeTicketsChart from "./free-ticket-chart";
import FreeTicketsBreakDownChart from "./free-tickets-breakdown";
import CustomAutocomplete from "src/shared/components/custom-auto-complete";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const ReportsSalesSummaryPage = () => {
  const [auth] = useRecoilState(authState);
  const { user, permissions } = auth;

  const defaultStartOfMonth = moment().startOf("month").hour(8).toDate();
  const initStartDate = defaultStartOfMonth;
  const initEndDate = moment().startOf("day").add(1, "days").hour(8).toDate();

  const maxDate = moment().add(1, "year").endOf("year").toDate();

  const dayOfJoining = user.clientJoiningDate
    ? new Date(user.clientJoiningDate)
    : null;

  const [selectedLocations, setSelectedLocations] = useState<any[]>([]);
  const [tempSelectedLocations, setTempSelectedLocations] = useState<any[]>([]);
  const [selectedService, setSelectedService] = useState<string>("all");
  const [tempSelectedService, setTempSelectedService] = useState<string>("all");
  const [tempStartDate, setTempStartDate] = useState<Date | null>(
    initStartDate,
  );
  const [startDate, setStartDate] = useState<Date | null>(initStartDate);
  const [tempEndDate, setTempEndDate] = useState<Date | null>(initEndDate);
  const [endDate, setEndDate] = useState<Date | null>(initEndDate);
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [openStartDate, setOpenStartDate] = useState<boolean>(false);
  const [openEndDate, setOpenEndDate] = useState<boolean>(false);

  // APIS
  const { data: locationsData, isLoading: isLoadingLocations } =
    useGetLocationsList({ isClient: true });
  const filters = {
    userId: user.ownerId,
    isClient: true,
    ...(selectedLocations.length > 0 &&
      selectedLocations.length < locationsData?.locations?.length && {
        locations: selectedLocations.map((loc) => loc.id),
      }),
    ...(selectedService !== "all" && { serviceName: selectedService }),
    ...(startDate && { startDate: startDate?.getTime() }),
    ...(endDate && { endDate: endDate?.getTime() }),
  };
  const { data, isLoading, error, status } = useGetSalesSummary(filters);
  const { data: servicesData } = useGetClientLocationsServices();

  const locationsServices = servicesData?.data ?? [];

  const selectedLocationIds = tempSelectedLocations.map((loc) => loc.id);

  const filteredLocationsServices =
    selectedLocationIds.length > 0
      ? locationsServices.filter((location) =>
          selectedLocationIds.includes(location.locationId),
        )
      : locationsServices;

  const uniqueServiceNames: string[] = Array.from(
    new Set(
      filteredLocationsServices.reduce((acc: any, location: any) => {
        location.services.forEach((service: any) => {
          acc.push(service.name);
        });

        return acc;
      }, []),
    ),
  );

  console.log(uniqueServiceNames);

  const handleChangeLocations = (value: any[], reason: string) => {
    setTempSelectedLocations(value);
    setTempSelectedService("all");
  };

  const handleChangeServiceType = (e: SelectChangeEvent) => {
    const value = e.target.value;
    setTempSelectedService(value);
  };

  const handleApply = () => {
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    setSelectedLocations(tempSelectedLocations);
    setSelectedService(tempSelectedService);
  };

  const handleResetFilers = () => {
    setStartDate(initStartDate);
    setEndDate(initEndDate);
    setSelectedLocations(sortedLocations);
    setSelectedService("all");

    setTempStartDate(initStartDate);
    setTempEndDate(initEndDate);
    setTempSelectedLocations(sortedLocations);
    setTempSelectedService("all");
  };

  const sortedLocations = useMemo(() => {
    return (
      locationsData?.locations.sort((a: any, b: any) =>
        a.nameEn?.localeCompare(b.nameEn),
      ) || []
    );
  }, [locationsData]);

  useEffect(() => {
    if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message:
            error?.response?.data?.message || "Oops! Something went wrong.",
        },
      ]);
    }
  }, [status]);

  useEffect(() => {
    if (sortedLocations.length > 0 && tempSelectedLocations.length === 0) {
      setTempSelectedLocations(sortedLocations);
      setSelectedLocations(sortedLocations);
    }
  }, [sortedLocations]);

  return (
    <Box p={{ xs: "20px 12px", md: "30px 20px", lg: "30px 30px" }}>
      <Typography fontWeight={600} fontSize={"22px"} mb={{ xs: 2, md: 4 }}>
        Dashboard
      </Typography>

      {/* filters */}
      <Stack direction={{ xs: "column", md: "row" }} gap={"10px"} mb={0.5}>
        <Stack direction={"row"} gap={"10px"} flexWrap={"wrap"} flexGrow={1}>
          {/* locations */}
          <CustomAutocomplete
            sx={{ width: { xs: "100%", sm: "20%" } }}
            size="small"
            limitTags={1}
            multiple
            id="tags-outlined"
            options={sortedLocations}
            disableCloseOnSelect
            getOptionLabel={(option: any) => option.nameEn}
            getOptionKey={(option: any) => option.id}
            renderOption={(props, option, { selected }) => (
              <li {...props} key={`${props.id} ${option.id}`}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                <Typography fontSize={"14px"}>{option.nameEn}</Typography>
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Location" placeholder="" />
            )}
            renderTags={(selected, getTagProps) =>
              selected.map((option: any, index: number) => (
                <Chip
                  size="small"
                  label={option.nameEn}
                  {...getTagProps({ index })}
                  key={option.id}
                />
              ))
            }
            value={tempSelectedLocations}
            onChange={(event, value, reason) => {
              handleChangeLocations(value, reason);
            }}
            loading={isLoadingLocations}
          />
          {/* service */}
          <FormControl
            fullWidth
            size="small"
            sx={{ width: { xs: "100%", sm: "15%" } }}
          >
            <InputLabel id="demo-simple-select-label">Service</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={tempSelectedService}
              label="Service"
              onChange={handleChangeServiceType}
              fullWidth
            >
              <MenuItem value={"all"}>All</MenuItem>
              {uniqueServiceNames.map((service) => {
                return (
                  <MenuItem key={service} value={service}>
                    {service}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          {/* service type */}
          {/* <FormControl
            fullWidth
            size="small"
            sx={{ width: { xs: "100%", sm: "15%" } }}
          >
            <InputLabel id="demo-simple-select-label">Service</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={tempSelectedService}
              label="Service"
              onChange={handleChangeServiceType}
              fullWidth
            >
              <MenuItem value={"all"}>All</MenuItem>
              <MenuItem value={"valet"}>Valet</MenuItem>
              <MenuItem value={"selfParking"}>Self Parking</MenuItem>
              <MenuItem value={"golfCar"}>Golf Cart</MenuItem>
            </Select>
          </FormControl> */}
          {/* start date */}
          <DateTimePicker
            label="Start Date"
            slotProps={{
              textField: {
                size: "small",
                onClick: () => setOpenStartDate(true),
                inputProps: { readOnly: true },
                sx: { width: "185px" },
              },
            }}
            format={"dd/MM/yyyy hh:mm aa"}
            value={tempStartDate}
            onChange={(newValue) => setTempStartDate(newValue)}
            monthsPerRow={4}
            name="start date"
            disableOpenPicker
            open={openStartDate}
            onClose={() => setOpenStartDate(false)}
            // minDate={minDate}
            maxDate={maxDate}
          />
          {/* end date */}
          <DateTimePicker
            label="End Date"
            slotProps={{
              textField: {
                size: "small",
                onClick: () => setOpenEndDate(true),
                inputProps: { readOnly: true },
                sx: { width: "185px" },
              },
            }}
            format={"dd/MM/yyyy hh:mm aa"}
            value={tempEndDate}
            onChange={(newValue) => setTempEndDate(newValue)}
            name="end date"
            disableOpenPicker
            open={openEndDate}
            onClose={() => setOpenEndDate(false)}
            // minDate={minDate}
            maxDate={maxDate}
          />
        </Stack>
        <Stack direction={"row"} gap={0.5}>
          <LoadingButton onClick={handleResetFilers}>Clear</LoadingButton>
          <LoadingButton
            loading={isLoading}
            variant="contained"
            onClick={handleApply}
            sx={{ width: "100px" }}
          >
            Apply
          </LoadingButton>
        </Stack>
      </Stack>

      {isLoading ? (
        <Stack gap={0.9} pb={2}>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17].map(
            (item) => (
              <Skeleton
                key={item}
                width={"100%"}
                height={"40px"}
                variant="rectangular"
              />
            ),
          )}
        </Stack>
      ) : (
        <>
          {!data ? (
            <NoData height={"540px"} />
          ) : (
            <>
              <Grid container mt={0} spacing={"20px"}>
                {permissions?.client_dashboard?.average_stay_number ? (
                  <Grid item xs={12} md={6} lg={3}>
                    <StatisticsBox
                      label="Average Stay Time Per Day"
                      value={`${hoursDecimalToHM(
                        data?.data?.overallSummary.averageParkingTimePerDay ??
                          0,
                      )}`}
                      percentage={Number(
                        data?.data?.averageParkingTimeChangePercentage ?? 0,
                      )}
                    />
                  </Grid>
                ) : null}
                {permissions?.client_dashboard?.free_tickets_number ? (
                  <Grid item xs={12} md={6} lg={3}>
                    <StatisticsBox
                      label="Free Tickets"
                      value={`${formatNumber(
                        data?.data?.overallSummary.freeTicketCount ?? 0,
                      )}`}
                      percentage={Number(
                        data?.data?.freeTicketsChangePercentage,
                      )}
                    />
                  </Grid>
                ) : null}

                {permissions?.client_dashboard?.total_sales_number ? (
                  <Grid item xs={12} md={6} lg={3}>
                    <StatisticsBox
                      label="Total Sales"
                      value={`${formatNumber(
                        data?.data?.overallSummary.totalSales ?? 0,
                      )}`}
                      percentage={Number(
                        data?.data?.totalSalesChangePercentage ?? 0,
                      )}
                      isMoney
                    />
                  </Grid>
                ) : null}
                {permissions?.client_dashboard?.total_cars_number ? (
                  <Grid item xs={12} md={6} lg={3}>
                    <StatisticsBox
                      label="Total No. of Cars"
                      value={`${formatNumber(
                        data?.data?.overallSummary.totalTickets ?? 0,
                      )}`}
                      percentage={Number(
                        data?.data?.totalTicketsChangePercentage ?? 0,
                      )}
                    />
                  </Grid>
                ) : null}
              </Grid>
              <Grid container mt={0} spacing={"20px"} mb={"20px"}>
                {permissions?.client_dashboard?.average_stay_charts ? (
                  <Grid item xs={12} lg={6}>
                    <AvgStayLinearChart data={data?.data?.parkingTimeByDay} />
                  </Grid>
                ) : null}
                {permissions?.client_dashboard?.average_stay_charts ? (
                  <Grid item xs={12} lg={6}>
                    <AvgStayBreakDownChart
                      data={data?.data?.parkingTimeByLocation}
                    />
                  </Grid>
                ) : null}

                {permissions?.client_dashboard?.free_tickets_charts ? (
                  <Grid item xs={12} lg={6}>
                    <FreeTicketsChart data={data?.data?.salesByDay} />
                  </Grid>
                ) : null}
                {permissions?.client_dashboard?.free_tickets_charts ? (
                  <Grid item xs={12} lg={6}>
                    <FreeTicketsBreakDownChart
                      data={data?.data?.freeTicketsByLocation}
                    />
                  </Grid>
                ) : null}

                {permissions?.client_dashboard?.total_sales_charts ? (
                  <Grid item xs={12} lg={6}>
                    <TotalSalesLinearChart data={data?.data?.salesByDay} />
                  </Grid>
                ) : null}
                {permissions?.client_dashboard?.total_sales_charts ? (
                  <Grid item xs={12} lg={6}>
                    <LocationsSalesChart data={data?.data?.salesByLocation} />
                  </Grid>
                ) : null}

                {permissions?.client_dashboard?.total_cars_charts ? (
                  <Grid item xs={12} lg={6}>
                    <TotalCarsChart data={data?.data?.salesByDay} />
                  </Grid>
                ) : null}
                {permissions?.client_dashboard?.total_cars_charts ? (
                  <Grid item xs={12} lg={6}>
                    <LocationsCarsChart data={data?.data?.salesByLocation} />
                  </Grid>
                ) : null}
              </Grid>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default ReportsSalesSummaryPage;
